// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

/**
 * Global tour context — keeps TourRunner mounted at layout level
 * so tours survive route navigation between modules.
 */

import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from 'react';
import type { TourScript } from '@vn-grc/shared';
import type { TourMode } from '../hooks/useTourEngine.js';

interface TourContextValue {
  activeScript: TourScript | null;
  activeTourMode: TourMode;
  startStepIndex: number;
  /** Monotonic counter — used as React key to force TourRunner remount. */
  launchId: number;
  launchTour: (script: TourScript, mode: TourMode, startStep?: number) => void;
  clearTour: () => void;
  setActiveTourMode: (mode: TourMode) => void;
}

const TourContext = createContext<TourContextValue | null>(null);

export function TourProvider({ children }: { children: ReactNode }) {
  const [activeScript, setActiveScript] = useState<TourScript | null>(null);
  const [activeTourMode, setActiveTourMode] = useState<TourMode>(
    (localStorage.getItem('grc_tour_mode') as TourMode) || 'autoplay',
  );
  const [startStepIndex, setStartStepIndex] = useState(0);
  const [launchId, setLaunchId] = useState(0);
  const counterRef = useRef(0);

  const launchTour = useCallback((script: TourScript, mode: TourMode, startStep = 0) => {
    counterRef.current += 1;
    setActiveScript(script);
    setActiveTourMode(mode);
    setStartStepIndex(startStep);
    setLaunchId(counterRef.current);
  }, []);

  const clearTour = useCallback(() => {
    setActiveScript(null);
    setStartStepIndex(0);
  }, []);

  return (
    <TourContext.Provider
      value={{
        activeScript,
        activeTourMode,
        startStepIndex,
        launchId,
        launchTour,
        clearTour,
        setActiveTourMode,
      }}
    >
      {children}
    </TourContext.Provider>
  );
}

export function useTourContext(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTourContext must be used within TourProvider');
  return ctx;
}
